/**
 * Roadmap Item Likes Service
 *
 * Domain layer for the roadmap-item likes/dislikes sheet. Datasource-agnostic:
 * all Firestore access is delegated to firestoreService.
 *
 * Mirrors Flutter's RoadmapRepository.getRoadmapItemLikes
 * (lib/data/repository/roadmap_repository.dart:102-109), a thin delegate that
 * forwards to the data source and returns the paginated like response. Here the
 * raw `roadmap_likes` page is remapped into lightweight grid-row entities via
 * the existing RoadmapItemLikeRemapper, with pagination state forwarded
 * unchanged. The sheet's `_getData`
 * (lib/presentation/bottom_sheet/roadmap_item_likes_bottom_sheet.dart:93-120)
 * reads only `like.userId` and `like.userName` from each row, so we map onto
 * the lean ContentLikedUserEntity ({ userId, username }) rather than a full
 * UserEntity.
 */

import type { PageCursor } from '@/data/types/json';
import { firestoreService } from '@/data/services/firestoreService';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { RoadmapItemLikeRemapper } from '@/domain/remappers/roadmapItemLike/roadmapItemLikeRemapper';
import type { ContentLikedUserEntity } from '@/domain/entities/contentReaction/contentLikedUserEntity';
import type { RoadmapItemLikeRequestDto } from '@/data/dtos/roadmapItemLike/roadmapItemLikeRequestDto';

export interface RoadmapItemLikedUsersPage {
  likedUsers: ContentLikedUserEntity[];
  hasMore: boolean;
  cursor?: PageCursor;
}

export class RoadmapItemLikesService {
  private static instance: RoadmapItemLikesService;

  public static getInstance(): RoadmapItemLikesService {
    if (!RoadmapItemLikesService.instance) {
      RoadmapItemLikesService.instance = new RoadmapItemLikesService();
    }
    return RoadmapItemLikesService.instance;
  }

  /**
   * Paginated fetch of users who liked (`liked = true`) or disliked
   * (`liked = false`) a given roadmap item. Remaps each raw `roadmap_likes`
   * document into a ContentLikedUserEntity via the existing
   * RoadmapItemLikeRemapper (reading `userId` and `userName`, mapping
   * `userName` → `username`) and forwards the pagination state (hasMore,
   * cursor) computed by the data layer. Unlike the content liked-users sheet,
   * Flutter applies NO blocked-user filter here
   * (roadmap_item_likes_bottom_sheet.dart has none), so the use case keeps this
   * a thin delegate.
   */
  async fetchRoadmapItemLikedUsers(
    roadmapItemId: string,
    liked: boolean,
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<RoadmapItemLikedUsersPage> {
    const page = await firestoreService.getRoadmapItemLikes(
      roadmapItemId,
      liked,
      limitCount,
      cursor,
    );
    return {
      likedUsers: page.items.map(item => {
        const dto = RoadmapItemLikeRemapper.fromJson(item);
        return { userId: dto.userId, username: dto.userName };
      }),
      hasMore: page.hasMore,
      cursor: page.cursor,
    };
  }

  /**
   * Persist a roadmap item like/dislike via the `saveRoadmapItemLike` Cloud
   * Function. Mirrors Flutter's RoadmapRepository delegating the write to its
   * cloud-functions data source. Returns the CF `success` flag.
   */
  async saveRoadmapItemLike(request: RoadmapItemLikeRequestDto): Promise<boolean> {
    return cloudFunctionsService.saveRoadmapItemLike(request);
  }
}

export const roadmapItemLikesService = RoadmapItemLikesService.getInstance();
